export const GTM_ID_PATTERN = /^GTM-[A-Z0-9]{4,12}$/;
export const DEFAULT_GTM_ID = "GTM-P585Q6KT";

export interface GtmExtra { id: string; paths: string[] }
export interface GtmRules { disabledPaths: string[]; extra: GtmExtra[] }
export interface GtmSettings { enabled: boolean; id: string; rules: GtmRules }

export const isValidGtmId = (id: string) => GTM_ID_PATTERN.test(id.trim());
/** A path rule is "/exact" or a prefix ending in "*". */
export const isValidPathRule = (p: string) => /^\/[A-Za-z0-9\-._~/]*\*?$/.test(p.trim());

const normalize = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : p);

export function pathMatches(path: string, rule: string): boolean {
  const r = rule.trim();
  if (!r) return false;
  const p = normalize(path);
  if (r.endsWith("*")) return p.startsWith(r.slice(0, -1)) || p === normalize(r.slice(0, -1));
  return p === normalize(r);
}

const strList = (v: unknown) =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && isValidPathRule(x)).map((x) => x.trim()) : [];

export function parseGtmRules(raw: unknown): GtmRules {
  try {
    const o = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!o || typeof o !== "object") return { disabledPaths: [], extra: [] };
    const r = o as { disabledPaths?: unknown; extra?: unknown };
    const extra = Array.isArray(r.extra)
      ? r.extra
          .filter((e): e is { id: string; paths: unknown } => !!e && typeof e === "object" && typeof (e as GtmExtra).id === "string" && isValidGtmId((e as GtmExtra).id))
          .map((e) => ({ id: e.id.trim(), paths: strList(e.paths) }))
      : [];
    return { disabledPaths: strList(r.disabledPaths), extra };
  } catch {
    return { disabledPaths: [], extra: [] };
  }
}

export function containersForPath(path: string, s: GtmSettings): string[] {
  if (!s.enabled) return [];
  const out: string[] = [];
  if (isValidGtmId(s.id) && !s.rules.disabledPaths.some((r) => pathMatches(path, r))) out.push(s.id.trim());
  for (const e of s.rules.extra) {
    if (e.paths.some((r) => pathMatches(path, r)) && !out.includes(e.id)) out.push(e.id);
  }
  return out;
}

/** Official GTM head snippet, once per container ID. */
export function injectGtm(id: string) {
  if (!isValidGtmId(id) || document.querySelector(`script[data-gtm-id="${id}"]`)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const j = document.createElement("script");
  j.async = true;
  j.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  j.setAttribute("data-gtm-id", id);
  const f = document.getElementsByTagName("script")[0];
  if (f?.parentNode) f.parentNode.insertBefore(j, f);
  else document.head.appendChild(j);
}
